import React, { useCallback, useState, useEffect, useRef } from 'react';
import { Menu, MenuItem, TextField } from '@mui/material';
import {
  WBBox,
  WBIconButton,
  WBConfirmationDialogue,
} from '@admiin-com/ds-web';
import UndoIcon from '../../../../../assets/icons/document-editor/arrow-counterclockwise.svg';
import RedoIcon from '../../../../../assets/icons/document-editor/arrow-clockwise.svg';
import BlockIcon from '../../../../../assets/icons/document-editor/text-paragraph.svg';
import FontFamilyIcon from '../../../../../assets/icons/document-editor/font-family.svg';
import MinusIcon from '../../../../../assets/icons/document-editor/minus-sign.svg';
import PlusIcon from '../../../../../assets/icons/document-editor/plus.svg';
import ColumnsIcon from '../../../../../assets/icons/document-editor/3-columns.svg';
import TableIcon from '../../../../../assets/icons/document-editor/table.svg';
import TrashIcon from '../../../../../assets/icons/document-editor/trash.svg';
// import ImageIcon from '../../../../../assets/icons/document-editor/file-image.svg';
import LinkIcon from '../../../../../assets/icons/document-editor/link.svg';
import AlignLeftIcon from '../../../../../assets/icons/document-editor/text-left.svg';
import AlignCenterIcon from '../../../../../assets/icons/document-editor/text-center.svg';
import AlignRightIcon from '../../../../../assets/icons/document-editor/text-right.svg';
import AlignJustifyIcon from '../../../../../assets/icons/document-editor/justify.svg';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import {
  $getSelection,
  $isRangeSelection,
  FORMAT_ELEMENT_COMMAND,
  UNDO_COMMAND,
  REDO_COMMAND,
  $createParagraphNode,
  $getRoot,
} from 'lexical';
import { $setBlocksType, $patchStyleText } from '@lexical/selection';
import {
  $createHeadingNode,
  $createQuoteNode,
  HeadingTagType,
} from '@lexical/rich-text';
import { $createCodeNode } from '@lexical/code';
import {
  INSERT_ORDERED_LIST_COMMAND,
  INSERT_UNORDERED_LIST_COMMAND,
} from '@lexical/list';
import { TOGGLE_LINK_COMMAND } from '@lexical/link';
import { $generateNodesFromDOM } from '@lexical/html';

import { FormatControls } from './FormatControls';
import { ColorControls } from './ColorControls';
import { TableControls, type TableControlsHandle } from './TableControls';
import { HandlebarsControls } from './HandlebarsControls';
import { InsertLayoutDialog } from './InsertLayoutDialog';
import { INSERT_LAYOUT_COMMAND } from '../plugins/LayoutPlugin';
import { useToolbarState } from '../hooks/useToolbarState';
import { useScrollShadows } from '../hooks/useScrollShadows';
import {
  StyledToolbarContainer,
  StyledToolbar,
  VerticalDivider,
} from '../DocumentEditor.styles';
import { useDocumentEditorContext } from '../context/DocumentEditorContext';
import {
  FONT_FAMILY_OPTIONS,
  BLOCK_TYPE_TO_BLOCK_NAME,
  MIN_ALLOWED_FONT_SIZE,
  MAX_ALLOWED_FONT_SIZE,
  DEFAULT_FONT_SIZE,
} from '../DocumentEditor.constants';

export const ToolbarPlugin: React.FC = () => {
  const [editor] = useLexicalComposerContext();
  const { toolbarState } = useToolbarState(editor);
  const { includeLetterhead, setIncludeLetterhead } =
    useDocumentEditorContext();
  const { ref: toolbarRef, shadows } = useScrollShadows();
  const { ref: hbRef, shadows: hbShadows } = useScrollShadows();

  const [blockMenuAnchor, setBlockMenuAnchor] = useState<null | HTMLElement>(
    null
  );
  const [fontFamilyMenuAnchor, setFontFamilyMenuAnchor] =
    useState<null | HTMLElement>(null);
  const [alignMenuAnchor, setAlignMenuAnchor] = useState<null | HTMLElement>(
    null
  );
  const [elementsMenuAnchor, setElementsMenuAnchor] =
    useState<null | HTMLElement>(null);
  const [fontColor, setFontColor] = useState(toolbarState.fontColor);
  const [bgColor, setBgColor] = useState(toolbarState.bgColor);
  const [layoutDialogOpen, setLayoutDialogOpen] = useState(false);
  const [clearDialogOpen, setClearDialogOpen] = useState(false);
  const tableControlsRef = useRef<TableControlsHandle>(null);

  useEffect(() => {
    setFontColor(toolbarState.fontColor);
    setBgColor(toolbarState.bgColor);
  }, [toolbarState.fontColor, toolbarState.bgColor]);

  const formatParagraph = useCallback(() => {
    editor.update(() => {
      const selection = $getSelection();
      if ($isRangeSelection(selection)) {
        $setBlocksType(selection, () => $createParagraphNode());
      }
    });
    setBlockMenuAnchor(null);
  }, [editor]);

  const formatHeading = useCallback(
    (headingSize: HeadingTagType) => {
      editor.update(() => {
        const selection = $getSelection();
        if ($isRangeSelection(selection)) {
          $setBlocksType(selection, () => $createHeadingNode(headingSize));
        }
      });
      setBlockMenuAnchor(null);
    },
    [editor]
  );

  const formatQuote = useCallback(() => {
    editor.update(() => {
      const selection = $getSelection();
      if ($isRangeSelection(selection)) {
        $setBlocksType(selection, () => $createQuoteNode());
      }
    });
    setBlockMenuAnchor(null);
  }, [editor]);

  const formatCode = useCallback(() => {
    editor.update(() => {
      const selection = $getSelection();
      if ($isRangeSelection(selection)) {
        $setBlocksType(selection, () => $createCodeNode());
      }
    });
    setBlockMenuAnchor(null);
  }, [editor]);

  const formatBulletList = useCallback(() => {
    editor.dispatchCommand(INSERT_UNORDERED_LIST_COMMAND, undefined);
    setBlockMenuAnchor(null);
  }, [editor]);

  const formatNumberedList = useCallback(() => {
    editor.dispatchCommand(INSERT_ORDERED_LIST_COMMAND, undefined);
    setBlockMenuAnchor(null);
  }, [editor]);

  const applyFontFamily = useCallback(
    (fontFamily: string) => {
      editor.update(() => {
        const selection = $getSelection();
        if ($isRangeSelection(selection)) {
          $patchStyleText(selection, {
            'font-family': fontFamily,
          });
        }
      });
      setFontFamilyMenuAnchor(null);
    },
    [editor]
  );

  const applyFontSize = useCallback(
    (size: string) => {
      const validSize = Math.max(
        MIN_ALLOWED_FONT_SIZE,
        Math.min(MAX_ALLOWED_FONT_SIZE, parseInt(size) || DEFAULT_FONT_SIZE)
      );
      editor.update(() => {
        const selection = $getSelection();
        if ($isRangeSelection(selection)) {
          $patchStyleText(selection, {
            'font-size': `${validSize}px`,
          });
        }
      });
    },
    [editor]
  );

  const updateFontSize = useCallback(
    (newSize: number) => {
      const validSize = Math.max(
        MIN_ALLOWED_FONT_SIZE,
        Math.min(MAX_ALLOWED_FONT_SIZE, newSize)
      );

      editor.update(() => {
        const selection = $getSelection();
        if ($isRangeSelection(selection)) {
          $patchStyleText(selection, {
            'font-size': `${validSize}px`,
          });
        }
      });

      setTimeout(() => {
        editor.getEditorState().read(() => {
          // Trigger toolbar state update
        });
      }, 0);
    },
    [editor]
  );

  const handleAlignmentChange = useCallback(
    (alignment: string) => {
      editor.dispatchCommand(FORMAT_ELEMENT_COMMAND, alignment as any);
      setAlignMenuAnchor(null);
    },
    [editor]
  );

  const handleClearDocument = useCallback(() => {
    editor.update(() => {
      const root = $getRoot();
      root.clear();
      const paragraph = $createParagraphNode();
      root.append(paragraph);
      paragraph.select();
    });
    editor.focus();
    setClearDialogOpen(false);
  }, [editor]);

  const insertLink = useCallback(() => {
    const url = prompt('Enter URL:');
    if (url) {
      editor.dispatchCommand(TOGGLE_LINK_COMMAND, url);
    }
  }, [editor]);

  const insertColumns = useCallback(() => {
    const parser = new DOMParser();
    const dom = parser.parseFromString(
      '<div class="columns"><div><br/></div><div><br/></div></div>',
      'text/html'
    );
    editor.update(() => {
      const nodes = $generateNodesFromDOM(editor, dom);
      const selection = $getSelection();
      if ($isRangeSelection(selection)) {
        selection.insertNodes(nodes);
      }
    });
    setElementsMenuAnchor(null);
  }, [editor]);

  const openTableModal = useCallback(() => {
    tableControlsRef.current?.openInsertTableModal();
    setElementsMenuAnchor(null);
  }, []);

  /* Image insertion temporarily disabled
  const insertImage = useCallback(() => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.onchange = () => {
      const file = input.files?.[0];
      if (file) {
        const reader = new FileReader();
        reader.onload = () => {
          const src = reader.result as string;
          const parser = new DOMParser();
          const dom = parser.parseFromString(
            `<img src="${src}" alt="image" />`,
            'text/html'
          );
          editor.update(() => {
            const nodes = $generateNodesFromDOM(editor, dom);
            const selection = $getSelection();
            if ($isRangeSelection(selection)) {
              selection.insertNodes(nodes);
            }
          });
        };
        reader.readAsDataURL(file);
      }
    };
    input.click();
    setElementsMenuAnchor(null);
  }, [editor]);
  */

  const openLayoutDialog = useCallback(() => {
    setLayoutDialogOpen(true);
    setElementsMenuAnchor(null);
  }, []);

  const handleLayoutSelect = useCallback(
    (templateColumns: string) => {
      editor.dispatchCommand(INSERT_LAYOUT_COMMAND, templateColumns);
    },
    [editor]
  );

  const getBlockTypeDisplay = () => {
    return (
      BLOCK_TYPE_TO_BLOCK_NAME[
        toolbarState.blockType as keyof typeof BLOCK_TYPE_TO_BLOCK_NAME
      ] || 'Normal'
    );
  };

  const getCurrentFontFamily = () => {
    return toolbarState.fontFamily || 'Arial';
  };

  const getAlignmentIcon = () => {
    switch (toolbarState.elementAlign) {
      case 'center':
        return <AlignCenterIcon />;
      case 'right':
        return <AlignRightIcon />;
      case 'justify':
        return <AlignJustifyIcon />;
      default:
        return <AlignLeftIcon />;
    }
  };

  const getAlignmentText = () => {
    switch (toolbarState.elementAlign) {
      case 'center':
        return 'Center';
      case 'right':
        return 'Right';
      case 'justify':
        return 'Justify';
      default:
        return 'Left';
    }
  };

  return (
    <StyledToolbarContainer>
      <StyledToolbar
        ref={toolbarRef}
        className={`${shadows.left ? 'show-left-shadow' : ''} ${
          shadows.right ? 'show-right-shadow' : ''
        }`}
      >
        {/* Undo/Redo */}
        <WBIconButton
          size="small"
          onMouseDown={(e) => {
            e.preventDefault();
            editor.dispatchCommand(UNDO_COMMAND, undefined);
          }}
          disabled={!toolbarState.canUndo}
          title="Undo"
          sx={{
            color: 'text.primary',
          }}
        >
          <UndoIcon />
        </WBIconButton>
        <WBIconButton
          size="small"
          onMouseDown={(e) => {
            e.preventDefault();
            editor.dispatchCommand(REDO_COMMAND, undefined);
          }}
          disabled={!toolbarState.canRedo}
          title="Redo"
          sx={{
            color: 'text.primary',
          }}
        >
          <RedoIcon />
        </WBIconButton>

        <VerticalDivider orientation="vertical" flexItem />

        {/* Block Type Dropdown */}
        <WBIconButton
          size="small"
          onClick={(event) => setBlockMenuAnchor(event.currentTarget)}
          title="Block Format"
          sx={{
            minWidth: 120,
            justifyContent: 'flex-start',
            color: 'text.primary',
          }}
        >
          <BlockIcon />
          &nbsp;{getBlockTypeDisplay()}
        </WBIconButton>
        <Menu
          anchorEl={blockMenuAnchor}
          open={Boolean(blockMenuAnchor)}
          onClose={() => setBlockMenuAnchor(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          PaperProps={{
            sx: {
              '& .MuiMenuItem-root': {
                fontSize: '12px',
                minHeight: 30,
                padding: '6px 12px',
                fontWeight: 500,
              },
            },
          }}
        >
          <MenuItem onClick={formatParagraph}>Normal</MenuItem>
          <MenuItem onClick={() => formatHeading('h1')}>Heading 1</MenuItem>
          <MenuItem onClick={() => formatHeading('h2')}>Heading 2</MenuItem>
          <MenuItem onClick={() => formatHeading('h3')}>Heading 3</MenuItem>
          <MenuItem onClick={formatBulletList}>Bullet List</MenuItem>
          <MenuItem onClick={formatNumberedList}>Numbered List</MenuItem>
          <MenuItem onClick={formatQuote}>Quote</MenuItem>
          <MenuItem onClick={formatCode}>Code Block</MenuItem>
        </Menu>

        <VerticalDivider orientation="vertical" flexItem />

        {/* Font Family Dropdown */}
        <WBIconButton
          size="small"
          onClick={(event) => setFontFamilyMenuAnchor(event.currentTarget)}
          title="Font Family"
          sx={{
            minWidth: 100,
            justifyContent: 'flex-start',
            color: 'text.primary',
          }}
        >
          <FontFamilyIcon />
          &nbsp;{getCurrentFontFamily()}
        </WBIconButton>
        <Menu
          anchorEl={fontFamilyMenuAnchor}
          open={Boolean(fontFamilyMenuAnchor)}
          onClose={() => setFontFamilyMenuAnchor(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          PaperProps={{
            sx: {
              '& .MuiMenuItem-root': {
                fontSize: '12px',
                minHeight: 30,
                padding: '6px 12px',
                fontWeight: 500,
              },
            },
          }}
        >
          {FONT_FAMILY_OPTIONS.map(([option, value]) => (
            <MenuItem key={option} onClick={() => applyFontFamily(value)}>
              {option}
            </MenuItem>
          ))}
        </Menu>

        {/* Font Size */}
        <WBBox sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
          <WBIconButton
            size="small"
            onMouseDown={(e) => {
              e.preventDefault();
              const currentSize =
                parseInt(toolbarState.fontSize) || DEFAULT_FONT_SIZE;
              updateFontSize(currentSize - 1);
            }}
            title="Decrease Font Size"
            sx={{
              minWidth: 24,
              height: 24,
              '& svg': { fill: 'currentColor', color: 'text.primary' },
            }}
          >
            <MinusIcon />
          </WBIconButton>

          <TextField
            size="small"
            value={toolbarState.fontSize}
            onChange={(e) => {
              const newValue = e.target.value;
              if (newValue === '' || /^\d+$/.test(newValue)) {
                if (newValue !== '' && newValue !== toolbarState.fontSize) {
                  const newSize = parseInt(newValue) || DEFAULT_FONT_SIZE;
                  updateFontSize(newSize);
                }
              }
            }}
            onBlur={(e) => {
              const newSize = parseInt(e.target.value) || DEFAULT_FONT_SIZE;
              updateFontSize(newSize);
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                const newSize =
                  parseInt((e.target as HTMLInputElement).value) ||
                  DEFAULT_FONT_SIZE;
                updateFontSize(newSize);
                (e.target as HTMLInputElement).blur();
              }
            }}
            sx={{ width: 50 }}
            inputProps={{
              min: MIN_ALLOWED_FONT_SIZE,
              max: MAX_ALLOWED_FONT_SIZE,
              style: { textAlign: 'center', fontSize: '14px' },
            }}
            placeholder="Size"
          />

          <WBIconButton
            size="small"
            onMouseDown={(e) => {
              e.preventDefault();
              const currentSize =
                parseInt(toolbarState.fontSize) || DEFAULT_FONT_SIZE;
              updateFontSize(currentSize + 1);
            }}
            title="Increase Font Size"
            sx={{ minWidth: 24, height: 24, color: 'text.primary' }}
          >
            <PlusIcon />
          </WBIconButton>
        </WBBox>

        <VerticalDivider orientation="vertical" flexItem />

        {/* Format Controls */}
        <FormatControls
          editor={editor}
          isBold={toolbarState.isBold}
          isItalic={toolbarState.isItalic}
          isUnderline={toolbarState.isUnderline}
          isStrikethrough={toolbarState.isStrikethrough}
        />

        {/* Link */}
        <WBIconButton
          onMouseDown={(e) => {
            e.preventDefault();
            insertLink();
          }}
          title="Insert Link"
          sx={{
            color: 'text.primary',
          }}
        >
          <LinkIcon />
        </WBIconButton>

        <VerticalDivider orientation="vertical" flexItem />

        {/* Color Controls */}
        <ColorControls
          editor={editor}
          fontColor={fontColor}
          bgColor={bgColor}
          setFontColor={setFontColor}
          setBgColor={setBgColor}
        />

        {/*<VerticalDivider orientation="vertical" flexItem />*/}

        {/* Alignment Dropdown */}
        <WBIconButton
          size="small"
          onClick={(event) => setAlignMenuAnchor(event.currentTarget)}
          title="Text Alignment"
          sx={{
            minWidth: 80,
            justifyContent: 'flex-start',
            color: 'text.primary',
          }}
        >
          {getAlignmentIcon()}
          <WBBox component="span" ml={1}></WBBox>
          {getAlignmentText()}
        </WBIconButton>
        <Menu
          anchorEl={alignMenuAnchor}
          open={Boolean(alignMenuAnchor)}
          onClose={() => setAlignMenuAnchor(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          PaperProps={{
            sx: {
              '& .MuiMenuItem-root': {
                fontSize: '12px',
                minHeight: 30,
                padding: '6px 12px',
              },
            },
          }}
        >
          <MenuItem onClick={() => handleAlignmentChange('left')}>
            <AlignLeftIcon />
            &nbsp;Left
          </MenuItem>
          <MenuItem onClick={() => handleAlignmentChange('center')}>
            <AlignCenterIcon />
            &nbsp;Center
          </MenuItem>
          <MenuItem onClick={() => handleAlignmentChange('right')}>
            <AlignRightIcon />
            &nbsp;Right
          </MenuItem>
          <MenuItem onClick={() => handleAlignmentChange('justify')}>
            <AlignJustifyIcon />
            &nbsp;Justify
          </MenuItem>
        </Menu>

        <VerticalDivider orientation="vertical" flexItem />

        <WBIconButton
          size="small"
          onClick={(event) => setElementsMenuAnchor(event.currentTarget)}
          title="Insert Element"
          sx={{ color: 'text.primary' }}
        >
          <PlusIcon />
        </WBIconButton>
        <Menu
          anchorEl={elementsMenuAnchor}
          open={Boolean(elementsMenuAnchor)}
          onClose={() => setElementsMenuAnchor(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          PaperProps={{
            sx: {
              '& .MuiMenuItem-root': {
                fontSize: '12px',
                minHeight: 30,
                padding: '6px 12px',
              },
            },
          }}
        >
          <MenuItem onClick={insertColumns}>
            <ColumnsIcon />
            &nbsp;Columns
          </MenuItem>
          <MenuItem onClick={openTableModal}>
            <TableIcon />
            &nbsp;Table
          </MenuItem>
          {/**
           * Image insertion temporarily disabled
           * <MenuItem onClick={insertImage}>
           *   <ImageIcon />
           *   &nbsp;Image
           * </MenuItem>
           */}
          <MenuItem onClick={openLayoutDialog}>
            <ColumnsIcon />
            &nbsp;Layout
          </MenuItem>
        </Menu>

        <VerticalDivider orientation="vertical" flexItem />

        <WBIconButton
          size="small"
          onMouseDown={(e) => {
            e.preventDefault();
            setClearDialogOpen(true);
          }}
          title="Clear document"
          sx={{ color: 'text.primary' }}
        >
          <TrashIcon />
        </WBIconButton>

        <TableControls
          ref={tableControlsRef}
          editor={editor}
          hideInsertButton
        />
      </StyledToolbar>
      <StyledToolbar
        ref={hbRef}
        className={`${hbShadows.left ? 'show-left-shadow' : ''} ${
          hbShadows.right ? 'show-right-shadow' : ''
        }`}
      >
        <HandlebarsControls editor={editor} />
      </StyledToolbar>

      {/* Layout Dialog - Assuming you have a dialog component for this */}
      {layoutDialogOpen && (
        <InsertLayoutDialog
          open={layoutDialogOpen}
          onClose={() => setLayoutDialogOpen(false)}
          onSelect={handleLayoutSelect}
        />
      )}

      <WBConfirmationDialogue
        open={clearDialogOpen}
        onClose={() => setClearDialogOpen(false)}
        onOK={handleClearDocument}
        title="Clear document?"
        okTitle="Clear"
      >
        This will remove all content from the document editor. This action
        cannot be undone.
      </WBConfirmationDialogue>
    </StyledToolbarContainer>
  );
};

export default ToolbarPlugin;
